import React, { useState, useCallback } from 'react';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import {
  ArrowUpTrayIcon,
  ExclamationTriangleIcon,
  CheckCircleIcon,
  DocumentTextIcon,
} from '@heroicons/react/24/outline';
import { turbineWorkspaceMappingsApi } from '@/services/api/turbineWorkspaceMappings';
import { ModalCard, StatsCard } from '../../components/ui';
import { CSVImportResult } from '@/types';
import { useCurrentUserId } from '@/hooks/useCurrentUserId';

interface CSVImportModalProps {
  isOpen: boolean;
  onClose: () => void;
}

const CSVImportModal: React.FC<CSVImportModalProps> = ({ isOpen, onClose }) => {
  const [file, setFile] = useState<File | null>(null);
  const [dragActive, setDragActive] = useState(false);
  const [importResult, setImportResult] = useState<CSVImportResult | null>(null);
  const [csvContent, setCsvContent] = useState<string>('');

  const queryClient = useQueryClient();
  const userId = useCurrentUserId();

  const importMutation = useMutation({
    mutationFn: (csvContent: string) => turbineWorkspaceMappingsApi.importMappingsCSV(csvContent, userId),
    onSuccess: (result) => {
      setImportResult(result);
      if (result.summary.successful_count > 0) {
        queryClient.invalidateQueries({ queryKey: ['turbines'] });
        queryClient.invalidateQueries({ queryKey: ['turbine-mappings'] });
        queryClient.invalidateQueries({ queryKey: ['mapping-stats'] });
      }
    },
    onError: (error: Error) => {
      alert(`Failed to import CSV: ${error.message}`);
    },
  });

  const handleFileChange = useCallback((file: File) => {
    setFile(file);
    setImportResult(null);

    const reader = new FileReader();
    reader.onload = (e) => {
      const content = e.target?.result as string;
      setCsvContent(content);
    };
    reader.readAsText(file);
  }, []);

  const handleDrop = useCallback(
    (e: React.DragEvent<HTMLDivElement>) => {
      e.preventDefault();
      e.stopPropagation();
      setDragActive(false);

      const files = e.dataTransfer.files;
      if (files && files.length > 0) {
        const file = files[0];
        if (file.type === 'text/csv' || file.name.endsWith('.csv')) {
          handleFileChange(file);
        } else {
          alert('Please select a CSV file');
        }
      }
    },
    [handleFileChange]
  );

  const handleDragOver = useCallback((e: React.DragEvent<HTMLDivElement>) => {
    e.preventDefault();
    e.stopPropagation();
    setDragActive(true);
  }, []);

  const handleDragLeave = useCallback((e: React.DragEvent<HTMLDivElement>) => {
    e.preventDefault();
    e.stopPropagation();
    setDragActive(false);
  }, []);

  const handleFileInput = (e: React.ChangeEvent<HTMLInputElement>) => {
    const files = e.target.files;
    if (files && files.length > 0) {
      handleFileChange(files[0]);
    }
  };

  const handlePreview = () => {
    if (csvContent) {
      importMutation.mutate(csvContent);
    }
  };

  const handleConfirmImport = async () => {
    // Here we should call API with create
    if (importResult && importResult.summary.successful_count > 0) {
      const res = await turbineWorkspaceMappingsApi.createConfigFromCSV(importResult.successful)
      if (res.total_rows === res.total_success) {
        alert(`Successfully imported ${res.total_success} mappings!`);
      } else {
        const warnings = res.warnings.join("/n")
        alert(`Successfully imported ${res.total_success} mappings out of ${res.total_rows}\n${res.total_fail} mappings failed.\n${warnings}`);
      }
      if (res.total_success > 0) {
        queryClient.invalidateQueries({ queryKey: ['turbines'] });
        queryClient.invalidateQueries({ queryKey: ['turbine-mappings'] });
        queryClient.invalidateQueries({ queryKey: ['mapping-stats'] });
      }
      handleClose();
    }
  };

  const handleClose = () => {
    setFile(null);
    setImportResult(null);
    setCsvContent('');
    onClose();
  };

  const generateSampleCSV = () => {
    const sampleData = [
      'Site Name,Turbine Name,Workspace Name,Component Name,Major Component Name',
      'Wind Farm Alpha,WFA-T01,Alpha Analysis Workspace,Gearbox,Drivetrain',
      'Wind Farm Alpha,WFA-T02,Alpha Analysis Workspace,Generator,Electrical',
      'Wind Farm Beta,WFB-T01,Beta Analysis Workspace,,Blade',
    ].join('\n');

    const blob = new Blob([sampleData], { type: 'text/csv' });
    const url = window.URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = 'sample-turbine-workspace-mappings.csv';
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    window.URL.revokeObjectURL(url);
  };

  return (
    <ModalCard
      isOpen={isOpen}
      onClose={handleClose}
      title="Import VBL Configuration from CSV"
      subtitle="Upload a CSV file to bulk configure VBL settings"
      size="lg"
    >
      <div className="space-y-6">
        {!importResult && (
          <>
            {/* File Upload Area */}
            <div className="space-y-4">
              <div className="flex justify-between items-center">
                <h3 className="text-lg font-medium text-gray-900">Upload CSV File</h3>
                <button
                  onClick={generateSampleCSV}
                  className="text-sm text-onyx-bright-blue hover:text-onyx-dark-blue"
                >
                  Download Sample CSV
                </button>
              </div>

              <div
                className={`relative border-2 border-dashed rounded-lg p-8 text-center transition-colors ${dragActive
                  ? 'border-onyx-bright-blue bg-blue-50'
                  : 'border-gray-300 hover:border-gray-400'
                  }`}
                onDrop={handleDrop}
                onDragOver={handleDragOver}
                onDragLeave={handleDragLeave}
              >
                <ArrowUpTrayIcon className="mx-auto h-12 w-12 text-gray-400" />
                <p className="mt-2 text-sm text-gray-600">
                  Drop your CSV file here, or{' '}
                  <label className="text-onyx-bright-blue cursor-pointer hover:text-onyx-dark-blue">
                    browse to select
                    <input
                      type="file"
                      accept=".csv"
                      onChange={handleFileInput}
                      className="hidden"
                    />
                  </label>
                </p>
                <p className="mt-1 text-xs text-gray-500">
                  CSV files only, maximum 10MB
                </p>
              </div>

              {file && (
                <div className="flex items-center p-3 bg-gray-50 rounded-lg">
                  <DocumentTextIcon className="h-5 w-5 text-gray-400 mr-2" />
                  <div className="flex-1">
                    <p className="text-sm font-medium text-gray-900">{file.name}</p>
                    <p className="text-xs text-gray-500">
                      {(file.size / 1024).toFixed(1)} KB
                    </p>
                  </div>
                </div>
              )}
            </div>

            {/* CSV Format Info */}
            <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
              <h4 className="text-sm font-medium text-blue-900 mb-2">Required CSV Format:</h4>
              <ul className="text-sm text-blue-800 space-y-1">
                <li>• <strong>Site Name</strong>: Name of the wind farm site</li>
                <li>• <strong>Turbine Name</strong>: Name/ID of the turbine</li>
                <li>• <strong>Workspace Name</strong>: Name of the analysis workspace</li>
                <li>• <strong>Component Name</strong>: Optional specific component</li>
                <li>• <strong>Major Component Name</strong>: Optional major component category</li>
              </ul>
            </div>

            {/* Actions */}
            <div className="flex justify-end space-x-3 pt-4 border-t">
              <button
                onClick={handleClose}
                className="px-4 py-2 text-sm text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-50"
              >
                Cancel
              </button>
              <button
                onClick={handlePreview}
                disabled={!file || !csvContent || importMutation.isPending}
                className="px-4 py-2 text-sm text-white bg-onyx-bright-blue rounded-lg hover:bg-onyx-dark-blue disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {importMutation.isPending ? 'Processing...' : 'Preview Import'}
              </button>
            </div>
          </>
        )}

        {/* Import Results */}
        {importResult && (
          <div className="space-y-6">
            <div className="text-center">
              <CheckCircleIcon className="mx-auto h-12 w-12 text-green-500" />
              <h3 className="mt-2 text-lg font-medium text-gray-900">Import Preview Complete</h3>
              <p className="mt-1 text-sm text-gray-600">
                Review the results below and confirm to proceed
              </p>
            </div>

            {/* Summary Stats */}
            <div className="grid grid-cols-3 gap-4">
              <StatsCard
                title="Total Rows"
                value={importResult.summary.total_rows}
                description="In CSV file"
                color="bright-blue"
              />
              <StatsCard
                title="Successful"
                value={importResult.summary.successful_count}
                description="Valid mappings"
                color="green"
              />
              <StatsCard
                title="Failed"
                value={importResult.summary.failed_count}
                description="Invalid rows"
                color="purple"
              />
            </div>

            {/* Failed Rows */}
            {importResult.failed.length > 0 && (
              <div className="bg-red-50 border border-red-200 rounded-lg p-4">
                <div className="flex-1">
                  <ExclamationTriangleIcon className="h-5 w-5 text-red-400" />
                  <div className="ml-3">
                    <h3 className="text-sm font-medium text-red-800">
                      {importResult.failed.length} row(s) failed validation
                    </h3>
                    <div className="mt-2 text-sm text-red-700 max-h-32 overflow-y-auto">
                      {importResult.failed.slice(0, 5).map((failure, index) => (
                        <div key={index} className="mb-2">
                          <p className="font-medium">
                            Row #{failure.lineNum}: {failure.row.site_name} - {failure.row.turbine_name}
                          </p>
                          <ul className="list-disc list-inside ml-4">
                            {failure.errors.map((error, errorIndex) => (
                              <li key={errorIndex}>{error}</li>
                            ))}
                          </ul>
                        </div>
                      ))}
                      {importResult.failed.length > 5 && (
                        <p className="text-xs italic">
                          ... and {importResult.failed.length - 5} more errors
                        </p>
                      )}
                    </div>
                  </div>
                </div>
              </div>
            )}

            {/* Actions */}
            <div className="flex justify-end space-x-3 pt-4 border-t">
              <button
                onClick={handleClose}
                className="px-4 py-2 text-sm text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-50"
              >
                Cancel
              </button>
              {importResult.summary.successful_count > 0 && (
                <button
                  onClick={handleConfirmImport}
                  className="px-4 py-2 text-sm text-white bg-green-600 rounded-lg hover:bg-green-700"
                >
                  Import {importResult.summary.successful_count} Mappings
                </button>
              )}
            </div>
          </div>
        )}
      </div>
    </ModalCard>
  );
};

export default CSVImportModal;